import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../Auth';
import { signInWithSocial } from '../../lib/auth-client';

function encodeEmailParam(email: string): string {
  return encodeURIComponent(email);
}

interface Props {
  // add your props here
}

const LoginForm: React.FC<Props> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  // Support both pathname string and full path with query string
  const fromPath = location.state?.from?.pathname || location.state?.from || '/';
  const from = typeof fromPath === 'string' ? fromPath : '/';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsPending(true);

    const formData = new FormData(event.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      const data = await signin({ email, password }, () => {
        // Navigation handled below based on email verification
      });

      // Check if email is verified from the login response
      const emailVerified = (data as any)?.user?.emailVerified ?? false;
      if (!emailVerified) {
        navigate(`/auth/verify-email?pending=true&email=${encodeEmailParam(email)}`, { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (authError: any) {
      if (authError?.code === 'EMAIL_NOT_VERIFIED') {
        navigate(`/auth/verify-email?pending=true&email=${encodeEmailParam(email)}`, { replace: true });
        return;
      }
      setError(authError.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="card w-full max-w-sm ">
      <div className="card-body">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="alert alert-error py-2 text-sm">
              <span className="material-icons text-sm">error</span>
              {error}
            </div>
          )}
          <div>
            <label htmlFor="email" className="label">
              <span className="label-text">Email</span>
            </label>
            <input
              type="text"
              id="email"
              name="email"
              placeholder="email"
              className="input"
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="label">
              <span className="label-text">Password</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="password"
              className="input"
              required
            />
          </div>
          <div className="flex justify-between items-center text-xs">
            <RouterLink
              to="/reset-password"
              className="text-on-surface-variant/40 hover:text-tertiary transition-colors"
            >
              Forgot password?
            </RouterLink>
            <RouterLink
              to="/signup"
              className="text-accent hover:text-accent-container transition-colors"
            >
              Don't have an account? Sign up
            </RouterLink>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={isPending}
          >
            {isPending ? 'Logging in...' : 'Login'}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-outline-variant/20"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-surface text-on-surface-variant/40">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              signInWithSocial('google').catch((err: any) => {
                setError(err?.message || 'Google sign-in failed. Please try again.');
              });
            }}
            className="btn btn-ghost w-full gap-3 border border-outline-variant/30 hover:bg-surface-container"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;